import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, ExternalLink, Building2 } from "lucide-react";
import VerifiedSenders from "@/components/VerifiedSenders";

export default function Settings() {
  const [form, setForm] = useState({ sender_email: "", sender_name: "", reply_to: "" });
  const [workspace, setWorkspace] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/workspace").catch(() => ({ data: null }))])
      .then(([s, w]) => { setForm(s.data); setWorkspace(w.data); setLoaded(true); });
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/settings", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (err) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="p-12 text-zinc-500 font-mono text-sm">// loading settings...</div>;

  return (
    <div className="p-10 max-w-5xl cf-fade">
      <div className="mb-10 pb-6 border-b border-zinc-800">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// config</div>
        <h1 className="font-display text-5xl font-bold uppercase mt-2 tracking-tight">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <form onSubmit={onSave} className="cf-card p-8">          <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">Sender Identity</h2>
          <p className="text-zinc-500 text-sm mb-6">Used as the From and Reply-To on every campaign you send.</p>

          <div className="space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
                Sender name (display name in inbox)
              </label>
              <input
                data-testid="settings-sender-name"
                type="text"
                value={form.sender_name || ""}
                onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                className="cf-input"
                placeholder="Jane Doe — appears before your email"
              />
              <div className="font-mono text-[10px] text-zinc-600 mt-2">// recipients see "Jane Doe &lt;hello@yourdomain.com&gt;" instead of just the email</div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
                Sender email (verified in SendGrid)
              </label>
              <input
                data-testid="settings-sender-email"
                type="email"
                required
                value={form.sender_email || ""}
                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                className="cf-input"
                placeholder="hello@yourdomain.com"
              />
              <div className="font-mono text-[10px] text-zinc-600 mt-2">// must be a verified single sender or be on a verified domain</div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
                Reply-to email (optional)
              </label>
              <input
                data-testid="settings-reply-to"
                type="email"
                value={form.reply_to || ""}
                onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                className="cf-input"
                placeholder="replies@yourdomain.com"
              />
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="settings-save" className="cf-btn-primary mt-8 w-full justify-center">
            <Save size={14} /> {busy ? "Saving..." : "Save Settings"}
          </button>
        </form>

        {/* Guide */}
        <div className="cf-card p-8">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">Verify Domain Guide</h2>
          <p className="text-zinc-500 text-sm mb-6">Inbox deliverability requires a verified sender in SendGrid.</p>

          <ol className="space-y-4 text-sm text-zinc-300">
            <li className="flex gap-3">
              <span className="font-mono text-[10px] text-[#E23A28] mt-1">01 //</span>
              <div>
                <strong className="text-white">Single Sender (fastest)</strong>
                <p className="text-zinc-500 mt-1">
                  In SendGrid → Settings → Sender Authentication → Single Sender Verification.
                  Add the address, confirm via inbox link, then enter it above.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[10px] text-[#E23A28] mt-1">02 //</span>
              <div>
                <strong className="text-white">Domain Authentication (recommended)</strong>
                <p className="text-zinc-500 mt-1">
                  Authenticate your sending domain. SendGrid generates CNAME records → add them to your DNS → click Verify.
                  Boosts inbox placement significantly.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[10px] text-[#E23A28] mt-1">03 //</span>
              <div>
                <strong className="text-white">Event Webhook</strong>
                <p className="text-zinc-500 mt-1">
                  In SendGrid → Settings → Mail Settings → Event Webhook, set the HTTP POST URL to:
                </p>
                <code className="block mt-2 p-2 bg-zinc-950 border border-zinc-800 font-mono text-[11px] text-zinc-300 break-all">
                  {process.env.REACT_APP_BACKEND_URL}/api/webhooks/sendgrid
                </code>
                <p className="text-zinc-500 mt-2 text-xs">
                  Enable events: Delivered, Bounced, Spam Reports, Opened, Clicked, Dropped. Save.
                </p>
              </div>
            </li>
          </ol>

          <a
            href="https://app.sendgrid.com/settings/sender_auth"
            target="_blank"
            rel="noreferrer"
            data-testid="sendgrid-dashboard-link"
            className="cf-btn-secondary mt-8 w-full justify-center"
          >
            Open SendGrid Dashboard <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {/* Verified Senders */}
      <VerifiedSenders onPickSender={async (email, name) => {
        setForm((f) => ({ ...f, sender_email: email, sender_name: name || f.sender_name }));
        try {
          await api.put("/settings", { sender_email: email, sender_name: name || form.sender_name, reply_to: form.reply_to });
          toast.success(`Set ${name ? name + " <" + email + ">" : email} as your sender`);
        } catch { /* ignore */ }
      }} />

      {/* Workspace section */}
      {workspace && (
        <div className="cf-card p-8 mt-8" data-testid="workspace-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                <Building2 size={11} /> // shared workspace
              </div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight mt-1">{workspace.name}</h2>
              <p className="text-zinc-500 text-sm mt-1">
                Everyone on these domains shares lists, campaigns, and stats.
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold">{workspace.member_count}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// members</div>
            </div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// domains</div>
          <div className="flex gap-2 flex-wrap mb-6">
            {workspace.domains?.map((d) => (
              <span key={d} className="cf-badge cf-badge-accent" data-testid={`workspace-domain-${d}`}>{d}</span>
            ))}
          </div>

          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// members</div>
          <div className="border border-zinc-800 divide-y divide-zinc-800">
            {workspace.members?.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-4 py-3" data-testid={`workspace-member-${m.id}`}>
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium truncate">{m.name}</div>
                  <div className="text-zinc-500 font-mono text-xs truncate">{m.email}</div>
                </div>
                <span className={`cf-badge ${m.role === "admin" ? "cf-badge-accent" : ""}`}>{m.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
